import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * PWA affordances that only make sense once the app is installable:
 *  - "New version available" prompt when a fresh service worker is waiting
 *    (otherwise users stay on the cached build until a manual hard refresh).
 *  - A tasteful "Install" chip driven by beforeinstallprompt.
 *  - An offline banner so failed reads read as "offline" not "broken".
 *
 * serviceWorkerRegistration.register() dispatches a window 'sw-update' event
 * (see index.tsx) carrying the waiting registration.
 */

const btnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  fontSize: 11,
  padding: '8px 14px',
  minHeight: 40,
  border: '1px solid var(--ink)',
  background: 'var(--ink)',
  color: 'var(--paper)',
  cursor: 'pointer',
  borderRadius: 0,
};

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const PWAPrompts: React.FC = () => {
  const [offline, setOffline] = useState<boolean>(
    typeof navigator !== 'undefined' && !navigator.onLine,
  );
  const [installEvt, setInstallEvt] = useState<BIPEvent | null>(null);

  // ── Service-worker update prompt ───────────────────────────────
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const reg = (e as CustomEvent<ServiceWorkerRegistration>).detail;
      toast.info(
        ({ closeToast }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: 'var(--font-body)' }}>
              A new edition of Trip&nbsp;Split is ready.
            </span>
            <button
              style={btnStyle}
              onClick={() => {
                const waiting = reg?.waiting;
                if (waiting) {
                  waiting.postMessage({ type: 'SKIP_WAITING' });
                  // Reload once the new SW takes control.
                  navigator.serviceWorker.addEventListener(
                    'controllerchange',
                    () => window.location.reload(),
                    { once: true },
                  );
                } else {
                  window.location.reload();
                }
                closeToast?.();
              }}
            >
              Reload to update
            </button>
          </div>
        ),
        { autoClose: false, closeOnClick: false, position: 'top-center' },
      );
    };
    window.addEventListener('sw-update', onUpdate as EventListener);
    return () => window.removeEventListener('sw-update', onUpdate as EventListener);
  }, []);

  // ── Install prompt ─────────────────────────────────────────────
  useEffect(() => {
    const onBIP = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BIPEvent);
    };
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // ── Online / offline ───────────────────────────────────────────
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const install = async () => {
    if (!installEvt) return;
    await installEvt.prompt();
    try {
      await installEvt.userChoice;
    } finally {
      setInstallEvt(null);
    }
  };

  return (
    <>
      {offline && (
        <div
          role="status"
          className="ts-mono ts-safe-x"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 4000,
            background: 'var(--stamp)',
            color: 'var(--paper)',
            textAlign: 'center',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            padding: 'calc(env(safe-area-inset-top) + 6px) 10px 6px',
          }}
        >
          Offline · showing your last-loaded ledger
        </div>
      )}

      {installEvt && (
        <button
          onClick={install}
          className="ts-safe-bottom"
          style={{
            position: 'fixed',
            left: 16,
            bottom: 16,
            zIndex: 1200,
            ...btnStyle,
            background: 'var(--paper)',
            color: 'var(--ink)',
            boxShadow: '0 10px 24px -12px rgba(20,17,13,0.5)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span aria-hidden style={{ fontSize: 14 }}>⤓</span> Install app
        </button>
      )}
    </>
  );
};

export default PWAPrompts;
